import { useEffect, useState } from 'react';
import type { StatsJSON, VersionStatistics } from '../About/types';

function formatStat(num: number | undefined, suffix = '+'): string {
  const n = num ?? 0;
  if (n <= 0) return '0';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M${suffix}`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1).replace(/\.0$/, '')}K${suffix}`;
  return `${n}${suffix}`;
}

export interface FormattedPricingStats {
  gene: string;
  variant: string;
  disease: string;
  gda_summary: string;
  vda_summary: string;
  dda: string;
  chemical: string;
}

export interface PricingStatsResult {
  formatted: FormattedPricingStats | null;
  statsLine: string;
  loading: boolean;
}

const DEFAULT_STATS_LINE = '30,300+ genes · 1.6M+ variants · 38,100+ diseases';
const DEFAULT_FORMATTED: FormattedPricingStats = {
  gene: '30,300+',
  variant: '1.6M+',
  disease: '38,100+',
  gda_summary: '2M+',
  vda_summary: '4.7M+',
  dda: '28M+',
  chemical: '12,000+',
};

function formatFromStatistics(s: VersionStatistics): FormattedPricingStats {
  return {
    gene: formatStat(s.gene ?? s.gda_gene),
    variant: formatStat(s.variant ?? s.vda_variant),
    disease: formatStat(s.gda_disease ?? s.disease),
    gda_summary: formatStat(s.gda_summary),
    vda_summary: formatStat(s.vda_summary),
    dda: formatStat(s.dda_summary ?? s.dda),
    chemical: formatStat(s.chemical ?? s.chemicals_gda_or_vda),
  };
}

function buildStatsLine(formatted: FormattedPricingStats): string {
  return `${formatted.gene} genes · ${formatted.variant} variants · ${formatted.disease} diseases`;
}

export function usePricingStats(): PricingStatsResult {
  const [formatted, setFormatted] = useState<FormattedPricingStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch('/stats.json')
      .then(res => res.json())
      .then((data: StatsJSON) => {
        if (cancelled) return;
        const current = data.current;
        const versionData = data[current];
        const stats =
          typeof versionData === 'object' && versionData && 'statistics' in versionData
            ? versionData.statistics
            : null;
        if (stats) {
          setFormatted(formatFromStatistics(stats));
        } else {
          setFormatted(DEFAULT_FORMATTED);
        }
      })
      .catch(() => {
        if (!cancelled) setFormatted(DEFAULT_FORMATTED);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const effective = formatted ?? DEFAULT_FORMATTED;
  const statsLine = formatted ? buildStatsLine(formatted) : DEFAULT_STATS_LINE;

  return { formatted: effective, statsLine, loading };
}
